import _ from 'underscore';
import moment from 'moment';
import { FormattingService } from '../services/formatting.service';
import { Injectable } from '@angular/core';
import { IColumnMetaData, IReportAggregates, IReportMetadata } from './report-data.service';
import { Dictionary } from '@type_definitions/vendor';
import { GaFormlyService, IExtraFieldConfigurationObject, OnChangeHandler } from '../formly/ga-formly.service';
import { util } from '../util';
import { IStoredReport, IStoredReportColumn } from './stored-report-data.service';
import { IObject } from '@type_definitions/datatypes';

interface IQueryFiltersOption {
  applyDefaultValues?:boolean;
  applyNullValues?:boolean;
  transformCommaSeparatedToArray?:boolean; // only applies to multilist screen type
}

interface IFiltersOptions {
  applyNullValues:boolean;
}

export const SCREENTYPE_DATERANGE:string = 'DATERANGE';
export const SCREENTYPE_LIST:string = 'LIST';
export const SCREENTYPE_LIST_MULTIPLE:string = 'MULTILIST';
export const SCREENTYPE_SEARCH:string = 'SEARCH';
export const TYPE_DATE:string = 'DATE';
export const TYPE_NUMBER:string = 'NUMBER';

/**
 * @ngdoc service
 * @name ga2.main.reports.util:ReportFiltersUtil
 *
 * @description
 * This util is used to handle filters and aggregate parameters of the reports
 *
 */
@Injectable()
export class ReportFiltersUtil {

  private static SEP1 = '\t';
  private static FDATE_REGEX = /(\d{1,2}|dd)\/(\d{1,2}|mm)\/(\d{4}|yyyy)/i; //e.g. 01/2/2002 or 31/12/YYYY

  constructor(
    private formattingService: FormattingService
  ) {
    /**/
  }

  /**
   * Processes the given filters by comparing them with default filters.
   * Returns an object that only contains filters which have values differing from the default filters.
   * Filters with values equal to the default filters are set to undefined.
   *
   * @param {IObject} filters - The object containing filters to be processed.
   * @param {IObject} defaultFilters - The object containing the default filter values for comparison.
   * @return {IObject} A new object with filters that differ from the default values, others set to undefined.
   */
  public processNonDefaultFilters(filters:IObject, defaultFilters:IObject):IObject {
    // we will only return filters that different from default filters values
    // otherwise we set the filters to undefined if it's the same as default filters values
    const realFilters:IObject = _.clone(filters);
    _.forEach(defaultFilters, (value:any, name:string) => {
      if (_.isEqual(realFilters[ name ], value)) {
        realFilters[ name ] = undefined;
      }
    });

    return realFilters;
  }

  public filtersToParam(filters:IObject, defaultFilters?:IObject, options?:IFiltersOptions):string | string[] {
    const paramObj:{ [ key:string ]:string } = {};

    _.each(filters, (value:any, name:string) => {
      if (util.notNullOrUndefined(value) && value !== '') {
        if (moment.isDate(value)) {
          paramObj[ name ] = this.formattingService.formatDate(value);
        } else {
          paramObj[ name ] = value;
        }
      } else if (options?.applyNullValues && value === null) {
        paramObj[ name ] = '';
      }
    });

    // add all filters which have been emptied by the user and have a different value than the default value
    if (defaultFilters) {
      defaultFilters = _.extend({}, defaultFilters); // convert array to object

      _.each(defaultFilters, (value:any, name:string) => {
        /**
         * Resets the filter parameter to an empty string if:
         * 1. The `filters[name]` value is `null` or `undefined`.
         * 2. The `value` (default filter value) is different from `filters[name]`.
         *
         * Updates `paramObj[name]` to an empty string in such cases to make sure it's being saved in url parameters.
         */
        if (util.isNullOrUndefined(filters[name]) && !_.isEqual(value, filters[name])) {
          paramObj[ name ] = '';
        }

        // remove the defaults from the selected filters
        if (_.isEqual(value, filters[name])) {
          delete paramObj[name];
        }
      });
    }

    // convert paramObj to an array of strings
    const param:string[] = [];
    _.each(paramObj, (value:any, name:string) => {
      param.push(name + ReportFiltersUtil.SEP1 + value);
    });

    return param.length? (param.length === 1? param[ 0 ] : param) : undefined;
  }

  public paramToFilters(longParamName:string|string[] = [],  shortParamName:string|string[] = []):IObject {
    const selectedFilters:IObject = {};
    longParamName = _.isArray(longParamName)? longParamName : [longParamName];
    shortParamName = _.isArray(shortParamName)? shortParamName : [shortParamName];

    const updateFiltersValue = (params:string[]):void => {
      _.forEach(params, (filter:string) => {
        if (filter && filter.indexOf(ReportFiltersUtil.SEP1) > 0) {
          const nameValue = filter.split(ReportFiltersUtil.SEP1);
          selectedFilters[ nameValue[ 0 ] ] = this.parseFilterValue(nameValue[ 1 ]);
        }
      });
    };

    updateFiltersValue(longParamName);
    updateFiltersValue(shortParamName);

    return selectedFilters;
  }

  private testFormattedDate(value:string):RegExpMatchArray {
    return _.isString(value) && value.match(ReportFiltersUtil.FDATE_REGEX);
  }

  private parseFormattedDate(value:string):string|Date {
    const matched = _.isString(value) && value.match(ReportFiltersUtil.FDATE_REGEX);

    if (!matched) {
      return value;
    }

    const now = moment();

    value = value.replace(/dd/i, now.date().toString());
    value = value.replace(/mm/i, (now.month() + 1).toString());
    value = value.replace(/yyyy/i, now.year().toString());

    return this.formattingService.parseDate(value);
  }

  public parseFilterValue(value:any):string|Date {
    let parsedValue:any;
    if (!value) {
      return value;
    }

    if (this.testFormattedDate(value)) {
      parsedValue = this.parseFormattedDate(value);
    } else {
      parsedValue = value;
    }
    return parsedValue;
  }

  /**
   * @ngdoc method
   * @name ga2.main.reports.util:ReportFiltersUtil#changedAggregatesToParam
   * @methodOf ga2.main.reports.util:ReportFiltersUtil
   *
   * @description Detect the aggregates that have been changed by comparing the default aggregates with the current aggregates comming from the model
   *
   * @param {IReportAggregates} defaultAggregates The list with the default aggregates and its values (retrieved from the server side)
   * @param {IReportAggregates} currentAggregates The list with the current aggregates and its values (selected by the user)
   *
   * @returns {string[]} An array containing the aggregate keys that have been changed
   * @example:
   * `defaultAggregates = {average: false, count: false, min: true, sum: true};`
   * `currentAggregates = {average: true, count: false, min: true, sum: false};`
   * `return = ['average','sum']`
   */
  public changedAggregatesToParam(defaultAggregates:IReportAggregates, currentAggregates:IReportAggregates):string[] {
    if (!defaultAggregates) {
      return [];
    }

    const changedAggregates:string[] = [];
    _.each(<Dictionary<any>>defaultAggregates, (defaultAggVal:boolean, defaultAggKey:string) => {
      if (defaultAggVal !== currentAggregates[ defaultAggKey ]) {
        changedAggregates.push(defaultAggKey);
      }
    });

    return changedAggregates;
  }

  /**
   * @ngdoc method
   * @name ga2.main.reports.util:ReportFiltersUtil#changedAggregatesFromParam
   * @methodOf ga2.main.reports.util:ReportFiltersUtil
   *
   * @description Detect the aggregates that have been changed by comparing the default aggregates with the current aggregates comming from the url parameter
   *
   * @param {IReportAggregates} defaultAggregates The list with the default aggregates and its values (retrieved from the server side)
   * @param {string[]} paramAggregates An array containing the aggregate keys that are different then defaultAggregates
   *
   * @returns {IReportAggregates} A list with the default aggregates by inverting their boolean value when their key is found in the paramAggregates array
   * @example:
   * `defaultAggregates = {average: false, count: false, min: true, sum: true};`
   * `param = ['average', 'sum']`
   * `return = {average: true, count: false, min: true, sum: false};`
   */
  public changedAggregatesFromParam(defaultAggregates:IReportAggregates, paramAggregates:string[]):IReportAggregates {
    if (!defaultAggregates) {
      return <IReportAggregates>{};
    }

    const changedAggregates:IReportAggregates = util.copyAndExtend(defaultAggregates);
    if (paramAggregates && paramAggregates.length > 0) {
      _.each(paramAggregates, (paramAggregate:string) => {
        changedAggregates[ paramAggregate ] = !changedAggregates[ paramAggregate ];
      });
    }

    return changedAggregates;
  }

  public aggregatesToParam(aggregates:string[]):string {
    if (!aggregates.length) {
      return undefined;
    }

    return aggregates.join(',');
  }

  public paramToAggregates(param:string[] | string):string[] {
    if (!param) {
      return [];
    }
    return _.isArray(param)? param : param.split(',');
  }

  public convertStoredReportToFilters(reportMetadata:IReportMetadata, storedReport:IStoredReport, options?:IQueryFiltersOption):any {
    const filtersObject:IObject = {};
    _.forEach(storedReport.columns, (storedColumn:IStoredReportColumn) => {
      // DATERANGE type
      if (this.screenType(reportMetadata, storedColumn.name) === 'DATERANGE') {
        filtersObject[ storedColumn.name + '_FROM' ] = storedColumn.filterValue;
        filtersObject[ storedColumn.name + '_TO' ] = storedColumn.filterValue2;
      } else {
        filtersObject[ storedColumn.name ] = storedColumn.filterValue;
      }
    });

    return this.getQueryFiltersFromModel(filtersObject, reportMetadata, {
      applyDefaultValues: util.notNullOrUndefinedOrEmpty(options?.applyDefaultValues) ? options.applyDefaultValues : false,
      applyNullValues: util.notNullOrUndefinedOrEmpty(options?.applyNullValues) ? options.applyNullValues : false,
      transformCommaSeparatedToArray: util.notNullOrUndefinedOrEmpty(options?.transformCommaSeparatedToArray) ? options.transformCommaSeparatedToArray : true
    });
  }

  public convertFiltersToStoredReport(reportMetadata:IReportMetadata, filters:any, visibleIndices:Dictionary<number> = {}):IStoredReportColumn[] {
    const storedColumns:IStoredReportColumn[] = [];
    _.each(filters, (value:string, key:string) => {
      const filterValue:string = moment.isDate(value)? this.formattingService.formatDate(value) : value;
      let filterValue1 = undefined, filterValue2 = undefined, columnName = key;

      // DATERANGE type
      if (key.match(/.*_FROM/)) {
        columnName = key.replace('_FROM', '');
        filterValue1 = filterValue;
      } else if (key.match(/.*_TO/)) {
        columnName = key.replace('_TO', '');
        filterValue2 = filterValue;
      } else {
        filterValue1 = filterValue;
      }

      let storedColumn:IStoredReportColumn = _.find(storedColumns, (storedColumn) => storedColumn.name === columnName);
      const storedFilterValue1:string = (storedColumn && storedColumn.filterValue) || filterValue1;
      const storedFilterValue2:string = (storedColumn && storedColumn.filterValue2) || filterValue2;

      if (!storedColumn) {
        const column = this.findColumnByName(reportMetadata, columnName);

        // if column not found because coming from url query filter params then don't store the column
        if (column) {
          storedColumn = createStoredColumn(column);
          storedColumns.push(storedColumn);

          // set value for daterange
          storedColumn.filterValue = storedFilterValue1;
          storedColumn.filterValue2 = storedFilterValue2;
        }
      } else {
        // set value for daterange
        storedColumn.filterValue = storedFilterValue1;
        storedColumn.filterValue2 = storedFilterValue2;
      }
    });

    //Add all column definitions that are not in the stored filters
    _.each(reportMetadata?.columns, (column:IColumnMetaData) => {
      if (!_.find(storedColumns, (storedColumn:IStoredReportColumn) => storedColumn.name === column.name)) {
        const storedColumn = createStoredColumn(column);
        storedColumns.push(storedColumn);
      }
    });

    //make the list indices natural (excluding the ones that have formatting.showinlist false
    //we also sort by name for columns that have the same sequence number
    //https://stackoverflow.com/questions/16426774/underscore-sortby-based-on-multiple-attributes
    //.sortBy(name).sortBy(listSequence) means ORDER BY listSequence ASC, name ASC
    _.chain(storedColumns)
      .filter(storedColumn => !_.isUndefined(storedColumn.listSequence))
      .sortBy(storedFilter => storedFilter.name)
      .sortBy(storedFilter => storedFilter.listSequence)
      .each((storedFilter, idx) => storedFilter.listSequence = idx) //temporarily set the index (to be used in next lambda)
      .sortBy(storedFilter => {
        const visibleIndex:number = visibleIndices[ storedFilter.name ];
        // if there is no visible index, use the previously sorted index
        return !_.isUndefined(visibleIndex)? visibleIndex : storedFilter.listSequence;
      })
      .each((storedFilter, idx) => storedFilter.listSequence = idx);

    //make the search indices natural (excluding the ones that have formatting.showinlist false
    _.chain(storedColumns)
      .filter(storedColumn => !_.isUndefined(storedColumn.searchSequence))
      .sortBy(storedFilter => storedFilter.name)
      .sortBy(storedFilter => storedFilter.searchSequence)
      .each((storedFilter, idx) => storedFilter.searchSequence = idx);

    // make array filter value into string comma separated
    _.chain(storedColumns)
      .filter((storedColumn:IStoredReportColumn) => _.isArray(storedColumn.filterValue))
      .each((storedColumn:IStoredReportColumn) => storedColumn.filterValue = storedColumn.filterValue.toString());

    return storedColumns;

    function createStoredColumn(column:IColumnMetaData):IStoredReportColumn {
      const storedColumn:IStoredReportColumn = {
        name: column.name,
        showInList: column.formatting.defaultinlist,
        showInSearch: column.formatting.showinsearch
      };

      if (column.formatting.showinlist) {
        storedColumn.listSequence = column.listSequence;
      }
      if (column.formatting.showinsearch) {
        storedColumn.searchSequence = column.searchSequence;
      }

      return storedColumn;
    }
  }

  private screenType(reportMetadata:IReportMetadata, columnName:string):string {
    const columnMetaData:IColumnMetaData = this.findColumnByName(reportMetadata, columnName);
    return columnMetaData && columnMetaData.screenType;
  }

  private findColumnByName(reportMetadata:IReportMetadata, columnName:string):IColumnMetaData {
    return _.chain(reportMetadata?.columns)
      .find((column:IColumnMetaData) => column.name === columnName)
      .value();
  }

  public getQueryFiltersFromModel(filterModel:any, reportMetadata:IReportMetadata, options?:IQueryFiltersOption):any {
    const applyDefaultAndNullValue = (screenType:string, filterValue:any, defaultValue:any, nullValue:any) => {
      if (util.notNullOrUndefinedOrEmpty(filterValue)) {
        if (screenType === SCREENTYPE_SEARCH) {
          return Number(filterValue) || undefined;
        } else if (screenType === SCREENTYPE_LIST_MULTIPLE) {
          // depends on the options param if transformCommaSeparatedToArray = true then it will transform to array otherwise to comma separated value
          return options.transformCommaSeparatedToArray? filterValue.split(',') : filterValue.toString();
        }

        return this.parseFilterValue(filterValue);
      }

      if (_.isUndefined(filterValue) && options.applyDefaultValues && !_.isUndefined(defaultValue)) {
        return this.parseFilterValue(defaultValue);
      }

      if (util.isNullOrUndefinedOrEmpty(filterValue) && options.applyNullValues && !_.isUndefined(nullValue)) {
        return this.parseFilterValue(nullValue);
      }
      return filterValue;
    };

    const queryFilters = {};
    _.forEach(reportMetadata?.columns, (column:IColumnMetaData) => {
      if (column.formatting && column.formatting.showinsearch) {
        const value = filterModel[ column.name ];
        if (column.screenType === 'DATERANGE') {
          const value1 = filterModel[ column.name + '_FROM' ];
          const value2 = filterModel[ column.name + '_TO' ];
          queryFilters[ column.name + '_FROM' ] = applyDefaultAndNullValue(column.screenType, value1, column.defaultValue1, column.nullValue1);
          queryFilters[ column.name + '_TO' ] = applyDefaultAndNullValue(column.screenType, value2, column.defaultValue2, column.nullValue2);
        } else {
          queryFilters[ column.name ] = applyDefaultAndNullValue(column.filterScreenType || column.screenType, value, column.defaultValue1, column.nullValue1);
        }
      }
    });
    return queryFilters;
  }

  // loads visibility and sequence of the stored filters into report metadata
  public updateColumnMetadata(reportMetaData:IReportMetadata, storedReport:IStoredReport) {
    _.forEach(storedReport.columns, (storedColumn:IStoredReportColumn) => {
      const columnMetaData:IColumnMetaData = this.findColumnByName(reportMetaData, storedColumn.name);
      if (columnMetaData) {
        columnMetaData.formatting.defaultinlist = storedColumn.showInList;
        columnMetaData.formatting.showinsearch = storedColumn.showInSearch;
        columnMetaData.searchSequence = storedColumn.searchSequence;
        columnMetaData.listSequence = storedColumn.listSequence;
        columnMetaData.groupingSequence = storedColumn.groupingSequence;
        columnMetaData.aggregateOn = storedColumn.aggregateOn;
      }
    });
  }

  public createDateRangeFilterFields(formlyService: GaFormlyService, label:string, key:string, changeHandler?: OnChangeHandler, labelClass?:string, valueClass?:string, wrapperClass:string = 'col-md-6'): IExtraFieldConfigurationObject {
    const fromValueClasses:string = 'daterange daterange-from';
    const separatorValueClasses:string = 'col col-auto daterange-separator';
    const toValueClasses:string = 'daterange daterange-to';
    const fromField:IExtraFieldConfigurationObject = formlyService.aCombinedDate(key + '_FROM', 'From')
      .withoutWrapper()
      .withWrapperClass('col')
      .withValueClass(fromValueClasses)
      .onChange(changeHandler)
      .build();
    const separator:IExtraFieldConfigurationObject = formlyService.aTemplate('<span class="col-form-label">to</span>')
      .withWrapperClass(separatorValueClasses)
      .build();
    const toField:IExtraFieldConfigurationObject = formlyService.aCombinedDate(`${key}_TO`, 'To')
      .withoutWrapper()
      .withWrapperClass('col')
      .withValueClass(toValueClasses)
      .onChange(changeHandler)
      .build();

    return formlyService.aGroup()
      .withEditable(key)
      .withLabel(label)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withWrapperClass(`formly-field-daterange ${wrapperClass || 'col-md-6'}`)
      .withFields([
        fromField,
        separator,
        toField
      ])
      .build();

  }

  public getDefaultFilters(columns:IColumnMetaData[]): string[] {
    const defaultFilters:string[] = [];

    // Default filter values are retrieved only if the column metadata is present in the search filters
    _.chain(columns)
      .filter((column:IColumnMetaData) => column.formatting.showinsearch)
      .each((column:IColumnMetaData) => {
        if (column.defaultValue1) {
          defaultFilters[ column.name ] = this.parseFilterValue(column.defaultValue1);
        }
        if (column.screenType === 'DATERANGE') {
          if (column.defaultValue1) {
            defaultFilters[ column.name + '_FROM' ] = this.parseFilterValue(column.defaultValue1);
          }
          if (column.defaultValue2) {
            defaultFilters[ column.name + '_TO' ] = this.parseFilterValue(column.defaultValue2);
          }
        }
      });

    return defaultFilters;
  }
}